import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import MusicPlayer from "./MusicPlayer";
import GameLoadOverlay from "./GameLoadOverlay";
import MainGameTutorial from "../tutorials/MainGameTutorial";
import { getMainTutorial } from "../tutorials/mainTutorials";
import { configuredLanguage } from "../tutorials/language";
import { beginUnityKeyboardListenerCapture } from "./unityKeyboardGuard";
import { installCanvasFullscreenFallback } from "./fullscreenFallback";
import { getGameAssetBasePath } from "./gameAssetPaths";
import { useGameByTitle, useGameAccess } from "../api/api_hooks";
import {
  postSessionEnd,
  postSessionEndBestEffort,
  postSessionGameFinished,
  postSessionKeepAlive,
} from "../api/api_funcs";
import { useRemotePage } from "../remote/RemoteControlContext";

const MAIN_TUTORIAL_ACTIONS = new Set(["previous", "next", "skip"]);

function isSafeGameName(name) {
  // Prevent path traversal / weird characters
  return typeof name === "string" && /^[a-zA-Z0-9_-]+$/.test(name);
}

function createClientSessionKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getClientSessionKey(gameName) {
  if (!gameName || typeof window === "undefined" || !window.sessionStorage) {
    return createClientSessionKey();
  }

  const storageKey = `game-player-client-session:${gameName}`;
  const existingKey = window.sessionStorage.getItem(storageKey);
  if (existingKey) return existingKey;

  const nextKey = createClientSessionKey();
  window.sessionStorage.setItem(storageKey, nextKey);
  return nextKey;
}

function formatGameAccessError(error) {
  const status = error?.response?.data?.status;
  const detail = error?.response?.data?.detail;

  if (status === "session_limit_reached") {
    return "You have reached your active session limit. Please close another game tab or try again in a moment.";
  }

  if (status === "denied_access") {
    return "You do not have access to this game.";
  }

  if (status === "invalid_client_session_key") {
    return "Could not create a game session. Please refresh the page and try again.";
  }

  if (error?.code === "ECONNABORTED") {
    return "Requesting the game session took too long. Please try again.";
  }

  return detail || error?.message || String(error);
}

function parseGameInfoResult(result) {
  if (result == null) return null;

  if (typeof result === "string") {
    const trimmed = result.trim();
    if (!trimmed) return null;

    try {
      return JSON.parse(trimmed);
    } catch {
      return { raw: trimmed };
    }
  }

  if (typeof result === "object") {
    if (Array.isArray(result) && result.length === 0) return null;
    if (!Array.isArray(result) && Object.keys(result).length === 0) return null;
    return result;
  }

  return { value: result };
}

function getUnityGameInfo(instance) {
  if (!instance) return null;

  try {
	instance.SendMessage("Manager", "GetGameInfo");
  } catch (error) {
    console.warn("Could not read Unity game info.", error);
  }

  return null;
}

function ExpandIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3.75h-4.5v4.5m12-4.5h4.5v4.5m0 7.5v4.5h-4.5m-7.5 0h-4.5v-4.5" />
    </svg>
  );
}

export function GamePlayerCore({
  gameKey = null,
  externalSessionId = null,
  tournamentMode = false,
  onReady = null,
  onUnityInstance = null,
  onGameFinished = null,
  onTutorialComplete = null,
}) {
	const { game: routeGame } = useParams();
	const navigate = useNavigate();
	const game = gameKey || routeGame;

  const safeGame = useMemo(() => {
    if (!isSafeGameName(game)) return null;
    return game;
  }, [game]);

  const gameTitle = useMemo(() => {
    if (!safeGame) return null;
    return safeGame
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, [safeGame]);

  const clientSessionKey = useMemo(() => getClientSessionKey(safeGame), [safeGame]);

  const { data: gameObj, isPending: isGamePending, isError: isGameError } = useGameByTitle(gameTitle);
  const { mutate: requestGameAccess, isPending: isGameAccessPending } = useGameAccess();

  const canvasRef = useRef(null);
  const canvasSlotRef = useRef(null);
  const unityHostRef = useRef(null);
  const unityInstanceRef = useRef(null);
  const unityKeyboardGuardCleanupRef = useRef(null);
  const scriptRef = useRef(null);
  const cssRef = useRef(null);
  const isDisposingUnityRef = useRef(false);
  const accessRequestGameIdRef = useRef(null);
  const accessSessionGameIdRef = useRef(null);
  const currentGameIdRef = useRef(null);
  const mountedRef = useRef(false);

  const [progress, setProgress] = useState(0);
  const [gameLoadStatus, setGameLoadStatus] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 1150, height: 720 });
  const [unityOverlayRoot, setUnityOverlayRoot] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isStageFullscreen, setIsStageFullscreen] = useState(false);
  const [remoteFullscreenRequested, setRemoteFullscreenRequested] = useState(false);
  const [musicRemoteState, setMusicRemoteState] = useState(null);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const musicRemoteActionsRef = useRef({});
  const completedTutorialIdentityRef = useRef(null);
  const openedTutorialIdentityRef = useRef(null);
  const controllersServer = process.env.REACT_APP_CONTROLLERS_SERVER || window.location.origin;
  const stageFullscreenRef = useRef(false);

  useEffect(() => {
    if (tournamentMode) setSessionId(externalSessionId || null);
  }, [externalSessionId, tournamentMode]);

  const base = useMemo(() => {
    return getGameAssetBasePath(safeGame);
  }, [safeGame]);

  const mainTutorial = useMemo(
    () => getMainTutorial(safeGame, configuredLanguage),
    [safeGame],
  );
  const tutorialIdentity = `${safeGame || "unknown"}:${sessionId || "pending"}:v${mainTutorial?.version || 0}`;

  const qrUrl = useMemo(() => {
    if (!controllersServer || !safeGame || !sessionId) return null;
    const trimmedServer = controllersServer.replace(/\/+$/, "");
    return `${trimmedServer}/${encodeURIComponent(safeGame)}/?sessionId=${encodeURIComponent(sessionId)}&lang=${encodeURIComponent(configuredLanguage)}`;
  }, [controllersServer, safeGame, sessionId]);

  const completeMainTutorial = React.useCallback((reason) => {
    if (!sessionId || completedTutorialIdentityRef.current === tutorialIdentity) return;
    completedTutorialIdentityRef.current = tutorialIdentity;
    setIsTutorialOpen(false);
    if (tournamentMode && mainTutorial) {
      try {
        window.sessionStorage.setItem(`main-game-tutorial:${tutorialIdentity}`, "complete");
      } catch {}
    }
    onTutorialComplete?.({
      gameKey: safeGame,
      sessionId,
      version: mainTutorial?.version || 0,
      reason,
    });
  }, [mainTutorial, onTutorialComplete, safeGame, sessionId, tournamentMode, tutorialIdentity]);

  useEffect(() => {
    if (!sessionId || gameLoadStatus === "error") {
      setIsTutorialOpen(false);
      return;
    }

    let restored = false;
    if (tournamentMode && mainTutorial) {
      try {
        restored = window.sessionStorage.getItem(`main-game-tutorial:${tutorialIdentity}`) === "complete";
      } catch {}
    }

    if (!mainTutorial || restored) {
      setIsTutorialOpen(false);
      completeMainTutorial(restored ? "restored" : "unavailable");
      return;
    }

    if (
      completedTutorialIdentityRef.current !== tutorialIdentity &&
      openedTutorialIdentityRef.current !== tutorialIdentity
    ) {
      openedTutorialIdentityRef.current = tutorialIdentity;
      setTutorialStepIndex(0);
      setIsTutorialOpen(true);
    }
  }, [completeMainTutorial, gameLoadStatus, mainTutorial, sessionId, tournamentMode, tutorialIdentity]);

  const handleTutorialAction = React.useCallback((action) => {
    if (!mainTutorial) return false;
    if (action === "skip") {
      completeMainTutorial("skipped");
      return true;
    }
    if (!isTutorialOpen) return false;
    if (action === "previous") {
      setTutorialStepIndex((current) => Math.max(0, current - 1));
      return true;
    }
    if (action === "next") {
      if (tutorialStepIndex >= mainTutorial.steps.length - 1) {
        completeMainTutorial("finished");
      } else {
        setTutorialStepIndex((current) => Math.min(mainTutorial.steps.length - 1, current + 1));
      }
      return true;
    }
    return false;
  }, [completeMainTutorial, isTutorialOpen, mainTutorial, tutorialStepIndex]);

  useEffect(() => {
    const handleControllerTutorialCommand = (event) => {
      const commandRoundId = event?.detail?.roundId;
      if (commandRoundId && String(commandRoundId) !== String(sessionId)) return;
      const action = String(event?.detail?.action || "").toLowerCase();
      if (!MAIN_TUTORIAL_ACTIONS.has(action)) return;
      if (event.cancelable) event.preventDefault();
      handleTutorialAction(action);
    };
    window.addEventListener("onscreen:tutorial-control", handleControllerTutorialCommand);
    return () => window.removeEventListener("onscreen:tutorial-control", handleControllerTutorialCommand);
  }, [handleTutorialAction, sessionId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    currentGameIdRef.current = gameObj?.id ?? null;
  }, [gameObj?.id]);

  const syncUnityHostPosition = React.useCallback(() => {
    const slot = canvasSlotRef.current;
    const host = unityHostRef.current;
    if (!slot || !host) return;

    if ((document.fullscreenElement && host.contains(document.fullscreenElement)) || stageFullscreenRef.current) {
      host.style.position = "fixed";
      host.style.left = "0";
      host.style.top = "0";
      host.style.width = "100vw";
      host.style.height = "100vh";
      return;
    }

    host.style.position = "absolute";
    const rect = slot.getBoundingClientRect();
    host.style.left = `${rect.left + window.scrollX}px`;
    host.style.top = `${rect.top + window.scrollY}px`;
    host.style.width = `${rect.width}px`;
    host.style.height = `${rect.height}px`;
  }, []);

  const activateStageFullscreen = React.useCallback(() => {
    stageFullscreenRef.current = true;
    setIsStageFullscreen(true);
    setIsFullscreen(true);
    setRemoteFullscreenRequested(false);
    window.requestAnimationFrame(syncUnityHostPosition);
  }, [syncUnityHostPosition]);

  const exitGameFullscreen = React.useCallback(async () => {
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch (fullscreenError) {
        console.warn("Could not exit native fullscreen.", fullscreenError);
      }
    }
    stageFullscreenRef.current = false;
    setIsStageFullscreen(false);
    setIsFullscreen(false);
    setRemoteFullscreenRequested(false);
    window.requestAnimationFrame(syncUnityHostPosition);
  }, [syncUnityHostPosition]);

  useEffect(() => {
    if (!isStageFullscreen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isStageFullscreen]);

  const cleanupUnity = React.useCallback(async () => {
    if (isDisposingUnityRef.current) return;

    isDisposingUnityRef.current = true;

    const instance = unityInstanceRef.current;
    unityInstanceRef.current = null;
    unityKeyboardGuardCleanupRef.current?.();
    unityKeyboardGuardCleanupRef.current = null;
    onUnityInstance?.(null);

    const originalAlert = window.alert;
    const suppressUnityTeardownAlert = (message) => {
      const text = String(message || "");
      if (
        text.includes("An error occurred running the Unity content") ||
        text.includes("Cannot read properties of null")
      ) {
        console.warn(text);
        return;
      }
      originalAlert(message);
    };
    window.alert = suppressUnityTeardownAlert;

    try {
      if (instance) {
        window.dispatchEvent(new Event("onscreen:unity-before-quit"));
      }
      const quitPromise = instance?.Quit?.();
      if (quitPromise && typeof quitPromise.then === "function") {
        await Promise.race([
          quitPromise,
          new Promise((resolve) => window.setTimeout(resolve, 5000)),
        ]);
      }
    } catch {}

    if (window.alert === suppressUnityTeardownAlert) {
      window.alert = originalAlert;
    }

    scriptRef.current?.remove();
    scriptRef.current = null;

    cssRef.current?.remove();
    cssRef.current = null;

    isDisposingUnityRef.current = false;
  }, [onUnityInstance]);

  useLayoutEffect(() => {
    const host = document.createElement("div");
    const canvas = document.createElement("canvas");
    const overlayRoot = document.createElement("div");

    host.style.cssText =
      "position:absolute;z-index:1000;display:block;overflow:hidden;background:#000;";
    canvas.id = "unity-canvas";
    canvas.tabIndex = 0;
    canvas.style.display = "block";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    const restoreCanvasFullscreen = installCanvasFullscreenFallback(canvas, activateStageFullscreen);

    overlayRoot.style.cssText =
      "position:absolute;inset:0;z-index:2;pointer-events:none;";
    host.appendChild(canvas);
    host.appendChild(overlayRoot);
    document.body.appendChild(host);
    unityHostRef.current = host;
    canvasRef.current = canvas;
    setUnityOverlayRoot(overlayRoot);
    syncUnityHostPosition();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(syncUnityHostPosition)
        : null;
    if (canvasSlotRef.current && resizeObserver) {
      resizeObserver.observe(canvasSlotRef.current);
    }

    window.addEventListener("resize", syncUnityHostPosition);
    window.addEventListener("scroll", syncUnityHostPosition, true);
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement && host.contains(document.fullscreenElement)) || stageFullscreenRef.current);
      syncUnityHostPosition();
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      window.removeEventListener("resize", syncUnityHostPosition);
      window.removeEventListener("scroll", syncUnityHostPosition, true);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      resizeObserver?.disconnect();
      restoreCanvasFullscreen();

      cleanupUnity().finally(() => {
        host.remove();
        if (unityHostRef.current === host) unityHostRef.current = null;
        if (canvasRef.current === canvas) canvasRef.current = null;
      });
    };
  }, [activateStageFullscreen, cleanupUnity, syncUnityHostPosition]);

  // access control: request sessionId when gameObj is ready and user
  useEffect(() => {
	if (tournamentMode) return;
    if (!gameObj?.id || !gameObj?.user_has_access) {
      accessRequestGameIdRef.current = null;
      accessSessionGameIdRef.current = null;
      setSessionId(null);
      return;
    }

    if (accessSessionGameIdRef.current === gameObj.id && sessionId) {
      return;
    }

    if (accessRequestGameIdRef.current === gameObj.id) {
      return;
    }

    const requestedGameId = gameObj.id;

    accessRequestGameIdRef.current = requestedGameId;
    setSessionId(null);
    setError("");

    requestGameAccess({
      gameId: requestedGameId,
      clientSessionKey,
    }, {
      onSuccess: (data) => {
        if (accessRequestGameIdRef.current === requestedGameId) {
          accessRequestGameIdRef.current = null;
        }
        const returnedSessionId = data?.sessionId;
        const requestIsCurrent = mountedRef.current && currentGameIdRef.current === requestedGameId;
        if (!requestIsCurrent) {
          if (returnedSessionId) {
            postSessionEnd(returnedSessionId).catch(() => {});
          }
          return;
        }

        accessSessionGameIdRef.current = requestedGameId;

        if (!returnedSessionId) {
          setGameLoadStatus("error");
          setError("Game access response is missing sessionId.");
          return;
        }

        setError("");
        setSessionId(returnedSessionId);
      },
      onError: (e) => {
        if (accessRequestGameIdRef.current === requestedGameId) {
          accessRequestGameIdRef.current = null;
        }
        if (!mountedRef.current) return;
        if (currentGameIdRef.current !== requestedGameId) return;

        setGameLoadStatus("error");
        setError(formatGameAccessError(e));
      },
    });
  }, [clientSessionKey, gameObj?.id, gameObj?.user_has_access, requestGameAccess, sessionId, tournamentMode]);

  // main game loading effect
  useEffect(() => {
    if (!base || !gameObj?.user_has_access || !sessionId) {
      return;
    }

    let cancelled = false;
    const ac = new AbortController();

    async function headOrGet(url) {
      // HEAD avoids downloading big files; fallback to GET if needed
      let res = await fetch(url, { method: "HEAD", cache: "no-store", signal: ac.signal }).catch(() => null);
      if (!res || !res.ok) {
        res = await fetch(url, { method: "GET", cache: "no-store", signal: ac.signal });
      }
      const ct = res.headers.get("content-type") || "";
      if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
      if (ct.includes("text/html")) throw new Error(`Got HTML instead of file for ${url}`);
      return res;
    }

    async function load() {
      setGameLoadStatus("loading");
      setProgress(0);
      setError("");

      await cleanupUnity();

      if (!window.OnScreenGamesNetworkConfig?.ready) {
        throw new Error("The shared network configuration loader is missing.");
      }
      await window.OnScreenGamesNetworkConfig.ready;

      const manifestRes = await fetch(`${base}/unity.json`, { cache: "no-store", signal: ac.signal });
      if (!manifestRes.ok) throw new Error(`unity.json missing for ${safeGame}`);
      const manifestContentType = manifestRes.headers.get("content-type") || "";
      if (!manifestContentType.includes("application/json")) {
        throw new Error(`Got ${manifestContentType || "an unknown content type"} instead of unity.json for ${safeGame}`);
      }
      const manifest = await manifestRes.json();

      if (cancelled) return;

      const loaderUrl = `${base}/${manifest.build.loader}`;
      const frameworkUrl = `${base}/${manifest.build.framework}`;
      const dataUrl = `${base}/${manifest.build.data}`;
      const wasmUrl = `${base}/${manifest.build.wasm}`;

      // Confirm files are served (you will see these in Network)
      await headOrGet(loaderUrl);
      await headOrGet(frameworkUrl);
      await headOrGet(wasmUrl);
      await headOrGet(dataUrl);

      // Load template css (optional)
      if (manifest.templateCss) {
        const css = document.createElement("link");
        css.rel = "stylesheet";
        css.href = `${base}/${manifest.templateCss}`;
        document.head.appendChild(css);
        cssRef.current = css;
      }

      // Ensure canvas id exists (Unity keyboard callbacks need it)
      const canvas = canvasRef.current;
      if (!canvas) throw new Error("Canvas ref is null");
      if (!canvas.id) canvas.id = "unity-canvas";

      // Load Unity loader script
      const script = document.createElement("script");
      script.src = loaderUrl;
      script.async = true;

      script.onload = async () => {
        let keyboardListenerCapture = null;
        let pendingKeyboardGuardCleanup = null;
        try {
          if (cancelled) return;

          if (typeof window.createUnityInstance !== "function") {
            throw new Error("createUnityInstance() not found after loader load");
          }

          const config = {
            dataUrl,
            frameworkUrl,
            codeUrl: wasmUrl,
            streamingAssetsUrl: `${base}/${manifest.build.streamingAssets || "StreamingAssets"}`,
            companyName: manifest.companyName,
            productName: manifest.productName,
            productVersion: manifest.productVersion,
            devicePixelRatio: Math.min(window.devicePixelRatio || 1, 2),
            matchWebGLToCanvasSize: true,
            errorHandler: (message) => {
              if (isDisposingUnityRef.current) return true;
              console.error(message);
              return false;
            },
          };

          keyboardListenerCapture = beginUnityKeyboardListenerCapture();
          const instance = await window.createUnityInstance(canvas, config, (p) => {
            if (!cancelled) setProgress(p);
          });
          pendingKeyboardGuardCleanup = keyboardListenerCapture.installGuards();
          keyboardListenerCapture = null;

          if (cancelled) {
            pendingKeyboardGuardCleanup();
            try {
              await instance?.Quit?.();
            } catch {}
            return;
          }

          unityInstanceRef.current = instance;
          unityKeyboardGuardCleanupRef.current?.();
          unityKeyboardGuardCleanupRef.current = pendingKeyboardGuardCleanup;
          pendingKeyboardGuardCleanup = null;
		  if (tournamentMode) window.__ZAHOOR_TOURNAMENT_ACTIVE__ = true;
          unityInstanceRef.current.SendMessage("Manager", "SetSessionId", sessionId);
		  onUnityInstance?.(instance);
          setGameLoadStatus("ready");
		  onReady?.(instance);
        } catch (e) {
          keyboardListenerCapture?.cancel();
          pendingKeyboardGuardCleanup?.();
          if (!cancelled) {
            setGameLoadStatus("error");
            setError(String(e?.message || e));
          }
        }
      };

      script.onerror = () => {
        if (!cancelled) {
          setGameLoadStatus("error");
          setError(`Failed to load loader script: ${loaderUrl}`);
        }
      };

      document.body.appendChild(script);
      scriptRef.current = script;

      // Optional: set canvas size from manifest
      const cw = manifest?.canvas?.width;
      const ch = manifest?.canvas?.height;
      if (cw && ch && canvasRef.current) {
        canvasRef.current.width = cw;
        canvasRef.current.height = ch;
        setCanvasSize({ width: cw, height: ch });
        window.requestAnimationFrame(syncUnityHostPosition);
      }
    }

    load().catch((e) => {
      if (!cancelled) {
        if (e?.name === "AbortError") return;
        setGameLoadStatus("error");
        setError(String(e?.message || e));
      }
    });

    return () => {
      cancelled = true;
      ac.abort();
      cleanupUnity();
    };
  }, [base, cleanupUnity, gameObj?.user_has_access, safeGame, sessionId, syncUnityHostPosition, tournamentMode, onReady, onUnityInstance]);

  // keep session alive every 2 minutes while game is ready
  useEffect(() => {
    if (gameLoadStatus !== "ready" || !sessionId) {
      return;
    }

    const intervalId = window.setInterval(() => {
      getUnityGameInfo(unityInstanceRef.current); // tells the game to emit the info
    }, 1 * 60 * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [gameLoadStatus, sessionId]);

  // Unity WebGL can dispatch: window.dispatchEvent(new CustomEvent("unity:game-info", { detail }))
  useEffect(() => {
    if (!sessionId) {
      return;
    }

    const handleGameInfoReceived = (event) => {
      const gameInfo = parseGameInfoResult(event.detail);
	  if (!gameInfo) {
		console.warn("Received game info event without game info.");
		return;
	  }
	  if (gameInfo.sessionId !== sessionId) {
		console.warn("Received game info event with mismatched sessionId.", {
		  expected: sessionId,
		  received: gameInfo.sessionId,
		});
		return;
	  }
	  
	  if (gameInfo.is_finished) {
		  if (tournamentMode) onGameFinished?.(gameInfo);
		  else postSessionGameFinished(sessionId, gameInfo).catch((error) => {
			console.warn("Could not send game finished event.", error);
		  });
	  }
	  else postSessionKeepAlive(sessionId, gameInfo).catch(() => {});
    };

    window.addEventListener("unity:game-info", handleGameInfoReceived);

    return () => {
      window.removeEventListener("unity:game-info", handleGameInfoReceived);
    };
  }, [sessionId, tournamentMode, onGameFinished]);

  // end session when tab closes or when active session changes/unmounts
  useEffect(() => {
	if (tournamentMode) return;
    if (!sessionId) {
      return;
    }

    const handleBeforeUnload = () => {
      postSessionEndBestEffort(sessionId);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      postSessionEnd(sessionId).catch(() => {});
    };
  }, [sessionId, tournamentMode]);

  const enterFullscreen = React.useCallback(async () => {
    const host = unityHostRef.current;
    if (!host) return;

    if (typeof host.requestFullscreen === "function") {
      try {
        await host.requestFullscreen();
        return;
      } catch (fullscreenError) {
        console.warn("Could not fullscreen the game host.", fullscreenError);
      }
    }
    activateStageFullscreen();
  }, [activateStageFullscreen]);

  const handleMusicSearchFocusChange = React.useCallback((isFocused) => {
    const webGLInput = unityInstanceRef.current?.Module?.WebGLInput;
    if (webGLInput) {
      webGLInput.captureAllKeyboardInput = !isFocused;
    }
  }, []);

  const handleMusicRemoteState = React.useCallback((nextState, actions) => {
    musicRemoteActionsRef.current = actions || {};
    setMusicRemoteState((current) => JSON.stringify(current) === JSON.stringify(nextState) ? current : nextState);
  }, []);

	useEffect(() => {
		const handleControllerMusicCommand = (event) => {
			const action = String(event?.detail?.action || "").toLowerCase();
			const actions = musicRemoteActionsRef.current;

			switch (action) {
				case "play":
					actions.play?.();
					break;
				case "pause":
					actions.pause?.();
					break;
				case "toggle":
					actions.togglePlayback?.();
					break;
				case "next":
					actions.next?.();
					break;
				case "previous":
					actions.previous?.();
					break;
				case "mute":
					actions.setMuted?.(true);
					break;
				case "unmute":
					actions.setMuted?.(false);
					break;
				case "volume": {
					const nextVolume = Number(event?.detail?.value);
					if (Number.isFinite(nextVolume)) {
						actions.setVolume?.(Math.max(0, Math.min(1, nextVolume)));
					}
					break;
				}
				default:
					console.warn("Ignoring unknown controller music command.", action);
			}
		};

		window.addEventListener("onscreen:music-control", handleControllerMusicCommand);
		return () => window.removeEventListener("onscreen:music-control", handleControllerMusicCommand);
	}, []);

  useRemotePage({
    enabled: !tournamentMode,
    id: "game-player",
    title: gameTitle || "Game player",
    subtitle: gameLoadStatus === "ready" ? "Game ready — gameplay uses the separate game QR" : gameLoadStatus === "error" || error ? "Game needs attention" : "Loading game",
    state: { gameLoadStatus, progress: Math.round(progress * 100), isFullscreen, sessionReady: Boolean(sessionId), music: musicRemoteState },
    sections: [
      { title: "Game screen", controls: [
        { id: "player.back", type: "button", label: "Back to games" },
        { id: "player.retry", type: "button", label: "Retry game", disabled: !(error || isGameError || gameLoadStatus === "error") },
        { id: "player.fullscreen", type: "button", label: isFullscreen ? "Exit fullscreen" : "Request fullscreen", disabled: gameLoadStatus !== "ready" },
      ] },
      ...(musicRemoteState?.tracks?.length ? [{ title: "Music", controls: [
        { id: "music.previous", type: "button", label: "Previous track" },
        { id: "music.toggle", type: "button", label: musicRemoteState.isPlaying ? "Pause music" : "Play music" },
        { id: "music.next", type: "button", label: "Next track" },
        { id: "music.muted", type: "toggle", label: "Mute", value: musicRemoteState.muted },
        { id: "music.volume", type: "range", label: "Volume", value: Math.round(musicRemoteState.volume * 100), min: 0, max: 100, unit: "%" },
        { id: "music.track", type: "select", label: "Track", value: musicRemoteState.currentTrackId, options: musicRemoteState.tracks },
      ] }] : []),
      ...(isTutorialOpen ? [{ title: "Tutorial", controls: [
        { id: "tutorial.previous", type: "button", label: "Previous", disabled: tutorialStepIndex === 0 },
        { id: "tutorial.next", type: "button", label: tutorialStepIndex === mainTutorial.steps.length - 1 ? "Start playing" : "Next" },
        { id: "tutorial.skip", type: "button", label: "Skip tutorial" },
      ] }] : []),
    ],
    actions: {
      "player.back": () => navigate("/"),
      "player.retry": () => window.location.reload(),
      "player.fullscreen": () => {
        if (isFullscreen) return exitGameFullscreen();
        setRemoteFullscreenRequested(true);
      },
      "music.previous": () => musicRemoteActionsRef.current.previous?.(),
      "music.toggle": () => musicRemoteActionsRef.current.togglePlayback?.(),
      "music.next": () => musicRemoteActionsRef.current.next?.(),
      "music.muted": ({ value }) => musicRemoteActionsRef.current.setMuted?.(Boolean(value)),
      "music.volume": ({ value }) => musicRemoteActionsRef.current.setVolume?.(Math.max(0, Math.min(1, Number(value) / 100))),
      "music.track": ({ value }) => musicRemoteActionsRef.current.selectTrackId?.(value),
      "tutorial.previous": () => handleTutorialAction("previous"),
      "tutorial.next": () => handleTutorialAction("next"),
      "tutorial.skip": () => handleTutorialAction("skip"),
    },
  });

  if (!base) {
    return <div className="mx-auto my-16 max-w-xl rounded-3xl border border-rose-400/25 bg-rose-500/10 p-8 text-center text-rose-100">Invalid game name.</div>;
  }

  const loadingMessage = isGamePending
    ? "Finding your game"
    : isGameAccessPending
      ? "Preparing a secure session"
      : "Loading the arena";
  const accessError = isGameError
    ? "We couldn't load this game's details."
    : (!isGamePending && gameObj && !gameObj.user_has_access)
      ? "You don't have access to this game."
      : error;
  const hasError = Boolean(accessError) || gameLoadStatus === "error";
  const canvasAspectRatio = canvasSize.width / canvasSize.height;
  const playerFrameMaxWidth = `min(100%, ${(canvasAspectRatio * 85).toFixed(2)}vh, ${Math.round(canvasAspectRatio * 960)}px)`;

  return (
    <div className="relative mx-auto w-full max-w-[1500px] flex-1 px-4 pb-20 pt-8 sm:px-6 lg:px-8 lg:pt-12">
      <div className="pointer-events-none absolute left-1/2 top-24 h-72 w-72 -translate-x-1/2 rounded-full bg-fuchsia-500/10 blur-3xl" />

      <header
        className="relative mx-auto mb-4 flex w-full flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
        style={{ maxWidth: playerFrameMaxWidth }}
      >
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-pink-300">
            <span className="h-2 w-2 rounded-full bg-pink-400 shadow-[0_0_12px_rgba(244,114,182,0.9)]" />
            Now playing
          </div>
          <h1 className="text-3xl font-black tracking-tight text-white sm:text-4xl">{gameTitle}</h1>
          <p className="mt-2 text-sm text-indigo-100/60">Your screen is the arena. Invite players and jump in.</p>
        </div>

        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-2 text-xs font-semibold text-indigo-100/75 backdrop-blur-xl">
          <span className={`h-2 w-2 rounded-full ${gameLoadStatus === "ready" ? "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.8)]" : hasError ? "bg-rose-400" : "animate-pulse bg-amber-300"}`} />
          {gameLoadStatus === "ready" ? "Game ready" : hasError ? "Needs attention" : "Getting ready"}
        </div>
      </header>

      {remoteFullscreenRequested && !isFullscreen ? (
        <div className="relative mx-auto mb-4 flex w-full items-center justify-between gap-4 rounded-2xl border border-cyan-300/30 bg-cyan-300/10 px-5 py-4 text-left" style={{ maxWidth: playerFrameMaxWidth }}>
          <div><strong className="text-white">Phone requested fullscreen</strong><p className="mt-1 text-xs text-indigo-100/65">Browsers require one click on this screen.</p></div>
          <div className="flex gap-2"><button type="button" onClick={() => { setRemoteFullscreenRequested(false); enterFullscreen(); }} className="rounded-xl bg-cyan-300 px-4 py-2 text-sm font-black text-slate-950">Enter</button><button type="button" onClick={() => setRemoteFullscreenRequested(false)} className="rounded-xl border border-white/15 px-3 py-2 text-sm font-bold text-white">Cancel</button></div>
        </div>
      ) : null}

      <section
        className="relative mx-auto w-full overflow-hidden rounded-[1.75rem] border border-white/10 bg-slate-900/70 p-2 shadow-2xl shadow-fuchsia-950/40 ring-1 ring-white/5 backdrop-blur-xl sm:p-3"
        style={{ maxWidth: playerFrameMaxWidth }}
      >
        <div className="flex items-center justify-between gap-4 px-3 py-2.5 sm:px-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex gap-1.5" aria-hidden="true">
              <span className="h-2.5 w-2.5 rounded-full bg-pink-400/80" />
              <span className="h-2.5 w-2.5 rounded-full bg-violet-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-indigo-400/70" />
            </div>
            <span className="truncate text-xs font-medium text-indigo-100/45">game arena / {safeGame}</span>
          </div>
          <button type="button" disabled={gameLoadStatus !== "ready"} onClick={enterFullscreen} className="group inline-flex shrink-0 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.07] px-3 py-2 text-xs font-bold text-white transition hover:border-pink-300/40 hover:bg-pink-400/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 disabled:cursor-not-allowed disabled:opacity-35" aria-label="Enter fullscreen mode">
            <ExpandIcon className="h-4 w-4 transition-transform group-hover:scale-110" />
            <span className="hidden sm:inline">Fullscreen</span>
          </button>
        </div>

        <div className="relative overflow-hidden rounded-[1.25rem] border border-white/10 bg-black shadow-inner shadow-black/60">
          <div ref={canvasSlotRef} className="w-full" style={{ aspectRatio: `${canvasSize.width} / ${canvasSize.height}` }} />
        </div>
      </section>

      {gameLoadStatus !== "ready" && unityOverlayRoot && createPortal(
        <GameLoadOverlay
          hasError={hasError}
          accessError={accessError}
          loadingMessage={loadingMessage}
          gameLoadStatus={gameLoadStatus}
          progress={progress}
        />,
        unityOverlayRoot
      )}

      {isTutorialOpen && unityOverlayRoot && createPortal(
        <MainGameTutorial
          tutorial={mainTutorial}
          stepIndex={tutorialStepIndex}
          onAction={handleTutorialAction}
        />,
        unityOverlayRoot
      )}

      {gameLoadStatus === "ready" && gameObj?.id && unityOverlayRoot && createPortal(
        <>
          <MusicPlayer
            gameId={gameObj.id}
            onSearchFocusChange={handleMusicSearchFocusChange}
            onRemoteStateChange={handleMusicRemoteState}
          />
          {isStageFullscreen && <button type="button" onClick={exitGameFullscreen} className="pointer-events-auto absolute right-4 top-4 z-50 rounded-xl border border-white/20 bg-slate-950/80 px-4 py-2 text-sm font-black text-white shadow-xl backdrop-blur" aria-label="Exit fullscreen mode">Exit fullscreen</button>}
        </>,
        unityOverlayRoot
      )}

      {!tournamentMode && !isFullscreen && gameLoadStatus === "ready" && qrUrl && createPortal(
        <div
          className="pointer-events-none fixed bottom-[15%] left-1/2 z-[1001] flex -translate-x-1/2 flex-col items-center gap-3 rounded-3xl border border-white/20 bg-slate-950/85 p-4 text-center text-white shadow-2xl backdrop-blur-xl"
        >
          <div className="rounded-2xl bg-white p-3"><QRCode value={qrUrl} size={isFullscreen ? 156 : 120} /></div>
          <div>
            <div className="text-xs font-black uppercase tracking-[0.18em] text-pink-300">Join game</div>
            <div className="mt-1 max-w-[170px] text-sm font-semibold leading-5 text-white/80">Scan with your phone to play</div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function GamePlayer() {
  return <GamePlayerCore />;
}
